import React, { useState, useSyncExternalStore } from 'react';
import {
  Button, Group, Stack, Text, TextInput,
} from '@mantine/core';
import {
  canGroupColumns,
  canMergeSelection,
  canUngroupColumns,
  canUnmergeSelection,
  groupColumns,
  rectToKeys,
  removeMergesInSelection,
  ungroupColumns,
} from '../../../../shared/tables/tableStructure';
import {
  clearTableSelection,
  getTableSelection,
  subscribeTableSelection,
} from '../../../utils/tableSelectionRegistry';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';

/**
 * « Structure » : fusionner des cellules, grouper des colonnes — les mêmes
 * gestes que dans le Proposal, avec le même moteur
 * (`src/shared/tables/tableStructure.js`) et le même endroit : le panneau
 * latéral, où le document les met aussi (`BoxFormControlsPortal`).
 *
 * La sélection, elle, se fait au canvas au Maj+clic ; elle transite par
 * `tableSelectionRegistry`, les deux vivant dans deux arbres React distincts.
 */

/** Une ligne ne porte de clé qu'à partir du moment où elle sert à une fusion. */
function withRowKeys(rows) {
  let changed = false;
  const next = (rows || []).map((row, idx) => {
    if (row?.key) return row;
    changed = true;
    return { ...row, key: `row-${idx}-${Math.random().toString(36).slice(2, 8)}` };
  });
  return changed ? next : rows;
}

function rowsWithFallbackKeys(rows) {
  return (rows || []).map((row, idx) => ({ ...row, key: row?.key ?? `row-${idx}` }));
}

export default function TableStructureField() {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const selection = useSyncExternalStore(subscribeTableSelection, getTableSelection);
  const [groupTitle, setGroupTitle] = useState('');

  const columns = itemProps.columns || [];
  const rows = itemProps.rows || [];
  const mergedCells = itemProps.mergedCells || [];
  const columnsDisposition = itemProps.columnsDisposition || [];

  // Tableau branché : ses fusions et ses groupes viennent de la table, comme
  // dans le Proposal (`isCustomTable`). Les composer à la main n'aurait pas de
  // suite — la prochaine lecture les remplacerait.
  const bound = !!itemProps.tableName;

  const mine = !bound && selection.blockId === itemProps.id ? selection : null;
  const keys = mine?.rect
    ? rectToKeys(rowsWithFallbackKeys(rows), columns, mine.rect)
    : null;
  const columnKeys = mine?.columnKeys || [];

  const canMerge = canMergeSelection(keys, mergedCells);
  const canUnmerge = canUnmergeSelection(keys, mergedCells);
  const canGroup = canGroupColumns(columnKeys, columns, columnsDisposition);
  const canUngroup = canUngroupColumns(columnKeys, columnsDisposition);

  const merge = () => {
    // Les clés de lignes sont posées et la fusion écrite dans le même dispatch :
    // une fusion qui référence des clés absentes des lignes serait ignorée au
    // rendu.
    const nextRows = withRowKeys(rows);
    const nextKeys = rectToKeys(rowsWithFallbackKeys(nextRows), columns, mine.rect);
    updateItemProps({ rows: nextRows, mergedCells: [...mergedCells, nextKeys] });
    clearTableSelection();
  };

  const unmerge = () => {
    updateItemProps({ mergedCells: removeMergesInSelection(mergedCells, keys) });
    clearTableSelection();
  };

  const group = () => {
    updateItemProps({
      columnsDisposition: groupColumns(
        columnsDisposition,
        columns,
        columnKeys,
        groupTitle.trim() || 'Groupe',
      ),
    });
    setGroupTitle('');
    clearTableSelection();
  };

  const ungroup = () => {
    updateItemProps({ columnsDisposition: ungroupColumns(columnsDisposition, columnKeys) });
    clearTableSelection();
  };

  if (bound) {
    return (
      <Text size="xs" c="dimmed">
        Les fusions et les groupes de colonnes viennent de la table du document.
      </Text>
    );
  }

  return (
    <Stack gap={8}>
      <Text size="xs" c="dimmed">
        Maj (⇧) + clic sur deux cellules pour retenir un rectangle, ou sur des
        en-têtes pour retenir des colonnes.
      </Text>

      <Group gap="xs">
        <Button size="compact-xs" variant="light" disabled={!canMerge} onClick={merge}>
          Fusionner
        </Button>
        <Button size="compact-xs" variant="light" disabled={!canUnmerge} onClick={unmerge}>
          Défusionner
        </Button>
      </Group>

      <Group gap="xs" align="flex-end">
        <TextInput
          size="xs"
          label="Titre du groupe"
          placeholder="Groupe"
          value={groupTitle}
          onChange={(e) => setGroupTitle(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button size="compact-xs" variant="light" disabled={!canGroup} onClick={group}>
          Grouper
        </Button>
        <Button size="compact-xs" variant="light" disabled={!canUngroup} onClick={ungroup}>
          Dégrouper
        </Button>
      </Group>
    </Stack>
  );
}
